import { useState } from 'react'
import type { ReactNode } from 'react'
import Button from '../../../shared/components/Button'

interface Column {
  key: string
  label: string
  render?: (item: Record<string, unknown>) => ReactNode
}

interface MetaTableProps {
  title: string
  items: Record<string, unknown>[]
  columns: Column[]
  isLoading: boolean
  addLabel?: string
  renderAddForm?: (onDone: () => void) => ReactNode
  onEdit?: (item: Record<string, unknown>) => void
  onDelete?: (item: Record<string, unknown>) => void
  editingId?: string | null
  renderEditForm?: (item: Record<string, unknown>, onDone: () => void) => ReactNode
}

export const MetaTable = ({
  title,
  items,
  columns,
  isLoading,
  addLabel = 'Add New',
  renderAddForm,
  onEdit,
  onDelete,
  editingId,
  renderEditForm,
}: MetaTableProps) => {
  const [showAddForm, setShowAddForm] = useState(false)

  return (
    <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-[18px] font-[600] text-foreground">{title}</h2>
        {renderAddForm && !showAddForm && (
          <Button
            size="sm"
            onClick={() => setShowAddForm(true)}
            icon={
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 3.33334V12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3.33334 8H12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            iconPosition="left"
          >
            {addLabel}
          </Button>
        )}
      </div>

      {showAddForm && renderAddForm && (
        <div className="mb-6 p-4 bg-muted rounded-[12px] border border-[#D9D9D9]">
          {renderAddForm(() => setShowAddForm(false))}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <svg className="animate-spin w-6 h-6" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#E5E7EB" strokeWidth="3" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="#3F3E42" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-[14px]">No items yet</div>
      ) : (
        <table className="w-full text-[14px]">
          <thead>
            <tr className="border-b border-[#E5E7EB]">
              {columns.map(col => (
                <th key={col.key} className="text-left pb-3 font-[600] text-muted-foreground">{col.label}</th>
              ))}
              <th className="text-right pb-3 font-[600] text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {items.map((item) => (
              <>
                <tr key={item.id as string} className="hover:bg-muted">
                  {columns.map(col => (
                    <td key={col.key} className="py-3 font-[500] text-foreground">
                      {col.render ? col.render(item) : String(item[col.key] ?? '')}
                    </td>
                  ))}
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {onEdit && (
                        <button
                          onClick={() => onEdit(item)}
                          className="p-1.5 rounded-[6px] hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                            <path d="M11.333 2a1.886 1.886 0 0 1 2.667 2.667L4.667 14H2v-2.667L11.333 2Z" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => onDelete(item)}
                          className="p-1.5 rounded-[6px] hover:bg-[#FECDCD] text-muted-foreground hover:text-[#E53E3E] transition-colors"
                        >
                          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                            <path d="M2 4h12M5.333 4V2.667h5.334V4M6.667 7.333v4M9.333 7.333v4M3.333 4l.667 9.333h8L12.667 4" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {editingId === (item.id as string) && renderEditForm && (
                  <tr key={`edit-${item.id}`}>
                    <td colSpan={columns.length + 1} className="py-3 px-0">
                      <div className="p-4 bg-muted rounded-[12px] border border-[#D9D9D9]">
                        {renderEditForm(item, () => onEdit?.(item))}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
